import React, { useCallback, useMemo } from "react";
import VStack from "../../ui_atoms/VStack";
import { View } from "react-native";
import {
    FeeType,
    Mission,
    MissionFee,
    Repetition,
    RepetitionType,
} from "../../__generated__/graphql";
import Switch from "../../ui_atoms/Switch";
import { useI18n } from "../../hooks/i18n-hooks";
import { Buildable } from "ts-essentials";
import { BodyLargeText, BodyMediumText } from "../FormattedText";
import Gap from "../../ui_atoms/Gap";
import { useTheme } from "../../hooks/use-theme";
import { formatCurrency } from "../../utils";
import { currentClientMissionFeesRate } from "../../contexts/CreateMissionManager";
import { useRecoilValue } from "recoil";
import MarkdownView from "../MarkdownView";

type MissionFeesProps = {
    mission: Buildable<Mission>;
    setFees: (fees: Buildable<MissionFee>[]) => void;
};

type FeeId = "hotel_apron" | "hotel_clothes";

type SelectionRowRowProps = {
    title: string;
    description: string;
    disabled?: boolean;
    onSelect: (state: boolean) => void;
    selected: boolean;
};

const SelectionRow: React.FC<SelectionRowRowProps> = ({
    title,
    description,
    selected,
    onSelect,
    disabled,
}) => {
    const { marginSize } = useTheme();
    return (
        <View
            style={{
                flexDirection: "row",
                alignItems: "flex-start",
            }}
        >
            <Switch
                style={{ paddingHorizontal: 0, marginRight: marginSize }}
                value={selected}
                onChange={onSelect}
                disabled={disabled}
            />
            <View style={{ flexShrink: 1 }}>
                <BodyLargeText style={{ flexShrink: 1 }}>{title}</BodyLargeText>
                <BodyMediumText style={{ flexShrink: 1 }}>{description}</BodyMediumText>
            </View>
        </View>
    );
};
const MissionHotellerieRestaurantFees = ({ mission, setFees }: MissionFeesProps) => {
    const i18n = useI18n();
    const CCT_FEES = useMemo(() => {
        return {
            hotel_apron: {
                amount: 2000,
                externalId: "hotel_apron",
                repetition: RepetitionType.WorkedMonth,
                type: FeeType.Cct,
            },
            hotel_clothes: {
                amount: 5000,
                externalId: "hotel_clothes",
                repetition: RepetitionType.WorkedMonth,
                type: FeeType.Cct,
            },
        };
    }, []);

    const repetitionTitle = useMemo(() => {
        switch (mission.currentContract?.workingTimeClassicSpec?.repetition) {
            case Repetition.None:
                return i18n.t("fees.cct.repetition.total");
            default:
                return i18n.t("fees.cct.repetition.month");
        }
    }, [i18n, mission.currentContract?.workingTimeClassicSpec?.repetition]);

    const currentClientMissionFeesRateValue = useRecoilValue(currentClientMissionFeesRate);

    const cctFees = useMemo(() => {
        return mission.currentContract?.fees?.filter((fee) => {
            return fee?.type === FeeType.Cct;
        });
    }, [mission.currentContract?.fees]);

    const hotelApronFee = useMemo(() => {
        return cctFees?.find((fee) => fee?.externalId === "hotel_apron");
    }, [cctFees]);

    const hotelClothesFee = useMemo(() => {
        return cctFees?.find((fee) => fee?.externalId === "hotel_clothes");
    }, [cctFees]);

    const onChangeFee = useCallback(
        (feeId: FeeId, state: boolean) => {
            // the goal here is to retrieve all the other fees that are not the one we are toggling
            // the we will be able to define the new array of fees by adding the one we are toggling
            const allOtherFees: MissionFee[] =
                (mission.currentContract?.fees?.filter(
                    (fee) => fee && fee?.externalId !== CCT_FEES[feeId].externalId,
                ) as MissionFee[]) || [];
            if (state) {
                setFees([
                    {
                        ...CCT_FEES[feeId],
                        invoiceAmount: Math.trunc(
                            (CCT_FEES[feeId].amount *
                                (100 + Number(currentClientMissionFeesRateValue))) /
                                100,
                        ),
                    },
                    ...(allOtherFees ? allOtherFees : []),
                ]);
            } else {
                setFees(allOtherFees ? allOtherFees : []);
            }
        },
        [setFees, CCT_FEES, currentClientMissionFeesRateValue, mission.currentContract?.fees],
    );
    return (
        <VStack gap={1} h="2/3">
            <MarkdownView
                markdown={i18n.t(
                    "screens.user.create_mission_hotellerieRestaurationFees.description",
                )}
            />

            <Gap gap={0} />
            <SelectionRow
                title={i18n.t("fees.cct.hotel_apron_title")}
                description={i18n.t("fees.cct.hotel_apron_description_2", {
                    repetition: repetitionTitle,
                    amount: formatCurrency({
                        amountInCents:
                            (CCT_FEES.hotel_apron.amount *
                                (100 + Number(currentClientMissionFeesRateValue))) /
                            100,
                    }),
                })}
                selected={!!hotelApronFee}
                onSelect={(state) => onChangeFee("hotel_apron", state)}
            />
            <SelectionRow
                title={i18n.t("fees.cct.hotel_clothes_title")}
                description={i18n.t("fees.cct.hotel_clothes_description_2", {
                    repetition: repetitionTitle,
                    amount: formatCurrency({
                        amountInCents:
                            (CCT_FEES.hotel_clothes.amount *
                                (100 + Number(currentClientMissionFeesRateValue))) /
                            100,
                    }),
                })}
                selected={!!hotelClothesFee}
                onSelect={(state) => onChangeFee("hotel_clothes", state)}
            />
        </VStack>
    );
};

export default MissionHotellerieRestaurantFees;
